import { INCENTIVE_PERMISSION_KEYS, OUTCOME_INCENTIVE_STATUS } from "../constants/IncentiveKeys.jsx";
import { INCENTIVE_FILTER_STORE_CONFIG_BY_FIELD_MAP, INCENTIVE_FILTER_ENTITY_TYPES } from "../constants/IncentiveKeys.jsx";
import { STORE_KEYS } from "../constants/StoreKeys.jsx";
import { INCENTIVE_POINT_STATUS } from "../constants/IncentiveKeys.jsx";
import { TAGS_KEYS } from "../constants/TagsKeys.jsx";
import { isDateRangeInsideLimit } from "./schedulerUtils.jsx";
import { DateHelper, MessageDialog } from "@bryntum/schedulerpro";
import { handleOnRecordDelete, performOperationWithHandlingRefreshes, processCellMenuItems } from "./gridCellMenuUtils.jsx";
import { EDITOR_KEYS } from "../constants/EditorKeys.jsx";
import { getOriginalRecord, openNewSpotIncentiveEditor } from "./utils.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards.jsx";
import { getIsArchivedForBoard } from "./otherUtils.jsx";
import { VIEW_TYPES } from "../constants/OtherKeys.jsx";

export const checkIncentivePermission = ({ project, permissionKey }) => {
  if (!project) return false;
  const { incentiveBoardPreferencesStore, boardUsersStore } = project;
  if (!incentiveBoardPreferencesStore || !boardUsersStore) return false;
  const { swivuCurrentUser } = boardUsersStore;

  const currentBoardUserRecord = boardUsersStore.find((boardUserRecord) => boardUserRecord.userId === swivuCurrentUser.id);
  const boardIncentivePermissions = incentiveBoardPreferencesStore.first;

  /* If no board incentive permissions record is found then return false,
  Bug: when the function is called at the time of reloading the project model then data is cleared and the first record is vanished, At that time if the function is called we get a crash
  */
  if (!boardIncentivePermissions) return false;

  const permissionValue = boardIncentivePermissions[permissionKey];

  /* If the permission value is array then it will hold user ids */
  if (Array.isArray(permissionValue)) {
    return permissionValue.includes(currentBoardUserRecord.id);
  }

  /* otherwise the permission value will be number and we need to give access to user if it matches the grants of the auth user */
  return swivuCurrentUser.boardPermissions.includes(permissionValue);
};

export const getIncentiveFilterOptions = (field, entityType, project) => {
  if (!field) return [];

  const { storeKey, recordKey } = INCENTIVE_FILTER_STORE_CONFIG_BY_FIELD_MAP[entityType][field];
  const store = project?.getCrudStore?.(storeKey);
  let data = [];

  /* Get options from tag values store */
  if ([STORE_KEYS.taskTagValuesStore, STORE_KEYS.resourceTagValuesStore].includes(storeKey)) {
    data = store.query((record) => record.tagSet === TAGS_KEYS[field], true);
  } else if ([STORE_KEYS.resourceTypeStore, STORE_KEYS.boardTeamsStore].includes(storeKey)) { /* Get options from other stores */
    data = store.query(() => true, true);
  }

  return data.map((record) => ({
    label: record[recordKey],
    value: record.id
  }));
};

export const transformIncentiveFilters = (filterRecords) => {
  return filterRecords?.map((filterRecord) => ({
    recordId: filterRecord.id,
    field: filterRecord.field,
    operator: filterRecord.operator,
    value: filterRecord.value,
    booleanOperator: filterRecord.booleanOperator
  })) || [];
};

export const handleUpdateIncentiveFilters = ({ store, resourceFilters, taskFilters, incentiveId, filtersToDelete }) => {
  const recordsToAdd = [];
  const filtersToRemove = [];
  const recordAddUpdateHandler = (filter, entityType, index) => {
    const { id, ...payload } = filter; // Remove react hook form's id from filter
    if (filter.recordId) {
      const filterRecord = store.getById(filter.recordId);
      if (filterRecord) filterRecord.set(payload);
    } else {
      const newFilterRecords = store.createRecord({
        ...payload,
        incentiveId,
        entityType,
        order: index + 1
      })
      recordsToAdd.push(newFilterRecords);
    }
  }

  const updateOrderOfRemainingFilters = (entityType) => {
    if (filtersToRemove.length < 1) return;
    const remainingFilters = store.query(record => record.incentiveId === incentiveId && record.entityType === entityType, true);
    remainingFilters.forEach((filter, index) => {
      filter.order = index + 1;
    })
  }

  resourceFilters?.forEach((filter, index) => recordAddUpdateHandler(filter, INCENTIVE_FILTER_ENTITY_TYPES.RESOURCE, index));
  taskFilters?.forEach((filter, index) => recordAddUpdateHandler(filter, INCENTIVE_FILTER_ENTITY_TYPES.TASK, index));

  filtersToDelete.forEach((filter) => {
    const filterRecord = store.getById(filter.recordId);
    if (filterRecord) filtersToRemove.push(filterRecord);
  });

  store.add(recordsToAdd);
  store.remove(filtersToRemove);

  /* Update order of the remaining filters */
  Object.values(INCENTIVE_FILTER_ENTITY_TYPES).forEach((entityType) => {
    updateOrderOfRemainingFilters(entityType);
  });

  return {
    recordsToAdd,
    filtersToRemove
  }
};

export function incentiveCanViewFilterFn(record) {
  const { project } = this;
  if (!project || record.isPhantom) return true;

  const hasManageIncentiveGrant = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS });
  const hasViewIncentiveGrant = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.INCENTIVE_VIEW });

  return hasManageIncentiveGrant || hasViewIncentiveGrant;
}

export function incentivePointCanViewFilterFn(record) {
  const { project } = this;
  if (!project || record.isPhantom) return true;

  const hasPendingGrant = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.PENDING_POINTS_VIEW });
  const hasApprovedGrant = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.APPROVED_POINTS_VIEW });
  const hasDeniedGrant = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.DENIED_POINTS_VIEW });
  const hasAwardedGrant = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.AWARDED_POINTS_VIEW });
  const isPointsApprover = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.POINT_APPROVERS });
  const isPointsAwarder = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.POINT_AWARDERS });

  const { PENDING, APPROVED, DENIED, AWARDED } = INCENTIVE_POINT_STATUS;

  switch (record.status) {
    case PENDING:
      return hasPendingGrant || isPointsApprover;
    case APPROVED:
      return hasApprovedGrant || isPointsApprover || isPointsAwarder;
    case DENIED:
      return hasDeniedGrant || isPointsApprover;
    case AWARDED:
      return hasAwardedGrant || isPointsAwarder;
    default:
      return false;
  }
}

export const calculateSpotIncentiveTotalPoints = ({ positiveTagIds = [], negativeTagIds = [], project }) => {
  if (!project?.incentiveTagsStore) return 0;

  const incentiveTagsStore = project.incentiveTagsStore;
  const allTags = incentiveTagsStore.query(() => true, true) || [];

  const positivePoints = positiveTagIds.reduce((sum, tagId) => {
    const tag = allTags.find(t => t.id === tagId);
    return sum + (tag?.categoryRecord?.points || 0);
  }, 0);

  const negativePoints = negativeTagIds.reduce((sum, tagId) => {
    const tag = allTags.find(t => t.id === tagId);
    return sum + (tag?.categoryRecord?.points || 0);
  }, 0);

  return positivePoints - negativePoints;
};


export const getEligibleResourceForSpotIncentive = ({ taskId, project, startDate, endDate }) => {
  const { resourceStore } = project;
  const eventRecords = new Map();
  const resources = resourceStore.query((record) => !record.isGroupHeader && record.eligibleBoardUsers.length > 0, true);
  resources.forEach((resource) => {
    resource.events.forEach((event) => {
      if (
        event.taskId === taskId &&
        isDateRangeInsideLimit(
          { startDate: event.startDate, endDate: event.endDate },
          { startDate, endDate },
        )
      ) {
        if (!eventRecords.has(event.resourceId)) {
          eventRecords.set(event.resourceId, {record: event, timeSpent: DateHelper.diff(event.startDate, event.endDate, "d")});
        } else {
          const existingEvent = eventRecords.get(event.resourceId);
          existingEvent.timeSpent += DateHelper.diff(event.startDate, event.endDate, "d");
        }
      }
    });
  });

  return Array.from(eventRecords.values());
};

export const isPointEligibleForStatusChange = (pointRecordStatus, project) => {
  const PENDING = INCENTIVE_POINT_STATUS.PENDING;
  const AWARDED = INCENTIVE_POINT_STATUS.AWARDED;
  const DENIED = INCENTIVE_POINT_STATUS.DENIED;
  const APPROVED = INCENTIVE_POINT_STATUS.APPROVED;

  const eligiblity = {
    [PENDING]: true,
    [APPROVED]: true,
    [DENIED]: true,
    [AWARDED]: true
  }

  /* Disable denied and approved if user doesn't have permission */
  const statusesToCheck = [DENIED, APPROVED];
  statusesToCheck.forEach((status) => {
    eligiblity[status] = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.POINT_APPROVERS })
  });

  /* Is eligible for pending if any other status is selected */
  if (pointRecordStatus !== PENDING) {
    eligiblity[PENDING] = false;
  }

  /* Disable awarded if user doesn't have permission or the status is pending or denied */
  if (!checkIncentivePermission({ project: project, permissionKey: INCENTIVE_PERMISSION_KEYS.POINT_AWARDERS }) || [PENDING, DENIED].includes(pointRecordStatus)) {
    eligiblity[AWARDED] = false;
  }

  /* If status is awarded then disable all statuses, can't go back from here or re-award. */
  if (pointRecordStatus === AWARDED) {
    [PENDING, APPROVED, DENIED, AWARDED].forEach((status) => eligiblity[status] = false);
  }

  return eligiblity;
}

export const handleIncentiveGridContextMenuOptions = (e, { editorHandler, user, project, grid }) => {
  const { items, record, selection } = e;
  const isMultiSelect = record.isGroupHeader || selection.length > 1;

  processCellMenuItems({
    entityName: "Point",
    editorHandler,
    grid,
    user,
    project,
    tagStoreKey: STORE_KEYS.clientTagValuesStore,
    promptOnRemove: true,
    onEditEntity(record) {
      editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, getOriginalRecord(record), { client: record });
    }
  })(e);

  /* Check permissions for archive and remove row */
  const { archive, removeRow, unArchive, status: statusItem } = items;
  archive.disabled = !checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS });
  removeRow.disabled = !checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.POINT_DELETERS });


  /* For single records whose status is pending we show remove button & hide archive/unarchive buttons */
  if (!isMultiSelect && record.status === INCENTIVE_POINT_STATUS.PENDING) {

    /* Hide archive & unarchive buttons */
    if (unArchive) unArchive.hidden = true;
    archive.hidden = true;

    /* Show remove row button */
    removeRow.hidden = false;

  } else if (record.isGroupHeader && record.swivuGroupColumn?.isIncentivePointsStatusColumn) {
    const groupedByStatus = record.groupChildren[0].status;
    if (groupedByStatus === INCENTIVE_POINT_STATUS.PENDING) {

      /* Hide archive & unarchive buttons */
      if (unArchive) unArchive.hidden = true;
      archive.hidden = true;

      /* Bug: for group records if we mutate the 'hidden' property of the 'removeRow' item then it doesn not take effect, Our changes are being overridden, This is only happening for group headers. Therefore to overcome this issue we are appending a new 'visible' 'removeRow2' item & making it work same as removeRow. */
      items.removeRow2 = {
        ...removeRow,
        onItem: handleOnRecordDelete(record.groupChildren, grid.store, true),
        hidden: false
      }
    }
  }

  const shouldShowStatusItem = record.isGroupHeader && record.groupChildren.length > 0 && record.swivuGroupColumn?.isIncentivePointsStatusColumn;
  if (shouldShowStatusItem) {
    statusItem.hidden = false; // Unhide main option
    const groupedByStatus = record.groupChildren[0].status; // Get status from the first record as the group record does not have the status value, Since every record in the group will have same value.

    statusItem.menu.items.forEach((statusMenuItem) => {
      if (groupedByStatus === statusMenuItem.value) {
        statusMenuItem.hidden = true;
        return;
      }

      const isEligible = isPointEligibleForStatusChange(groupedByStatus, project)[statusMenuItem.value];
      statusMenuItem.disabled = !isEligible;

      statusMenuItem.onItem = async () => {
        const pointRecords = [...record.groupChildren];
        const newStatus = statusMenuItem.value;

        // If changing to AWARDED status, open the Award Tag modal
        if (newStatus === INCENTIVE_POINT_STATUS.AWARDED && editorHandler) {
          editorHandler.openEditor(EDITOR_KEYS.awardTagEditor, null, {
            pointRecords,
          }, { by: "recordType", useExisting: false });
        }

        await performOperationWithHandlingRefreshes(grid, () => {
          pointRecords.forEach((pointRecord) => {
            pointRecord.status = statusMenuItem.value;
          })
        })();
      }
    })
  }
}

export const isTaskScheduledInPastOrToday = (record) => {
  const now = new Date();
  return record.events?.some((event) => event.startDate <= now) ?? false;
};

export const openAdjustPointsEditorWithGuard = ({ project, editorHandler, record, otherParams = {} }) => {
  if (!checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS })) {
    MessageDialog.alert({
      title: "Cannot Adjust Points",
      message: "You do not have permission to adjust points on this board."
    });
    return;
  }
  if (!isTaskScheduledInPastOrToday(record)) {
    MessageDialog.alert({
      title: "Cannot Adjust Points",
      message: "No scheduled events have started for this task. Points can only be adjusted once at least one event has begun."
    });
    return;
  }
  openNewSpotIncentiveEditor({ project, editorHandler, otherParams: { selectedTask: record, ...otherParams } });
};

export const getOutcomeIncentiveMenuItems = (project, editorHandler, selectedViewType) => {
  if (selectedViewType !== VIEW_TYPES.incentivePoints) return null;

  const { outcomeIncentiveStore } = project;

  const createSubMenuItems = (status) => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
    const records = outcomeIncentiveStore.query(
      (record) => record.status === status && !record.isPhantom && (canShowArchivedRecords || !record.archived),
      true
    );
    return records.map((record) => ({
      id: record.id,
      text: record.name,
      archived: record.archived,
      onClick: () => {
        editorHandler.openEditor(EDITOR_KEYS.outcomeIncentiveEditor, record, { incentiveRecord: record });
      }
    }));
  };

  const activeItems = createSubMenuItems(OUTCOME_INCENTIVE_STATUS.ACTIVE);
  const inactiveItems = createSubMenuItems(OUTCOME_INCENTIVE_STATUS.PAUSED);

  return {
    active: {
      text: `Active (${activeItems.length})`,
      items: activeItems,
      disabled: activeItems.length === 0
    },
    inactive: {
      text: `Inactive (${inactiveItems.length})`,
      items: inactiveItems,
      disabled: inactiveItems.length === 0
    }
  };
}
